import type { Filter } from '../types/common.types'
import type { ContentItemsFilter } from '../types/content-items.types'

import { contentTypesField } from './contentTypesField'
import {
  booleanField,
  entityField,
  numberField,
  type QueryFilterSchema,
  stringArrayField,
  stringField,
} from './useQueryFilter'

type CommonFilterShape = Filter & Pick<ContentItemsFilter, 'contentTypes'>

/**
 * Reusable codecs matching the URL conventions of the legacy `useInitialFilter` /
 * `useSyncFilterWithQuery` hooks. Both admin-app-v2 and advertiser-app listing pages
 * compose these into a per-page schema via `useQueryFilter`.
 *
 * URL keys mirror the legacy serializer exactly, so existing bookmarked URLs
 * continue to work.
 *
 * Pass per-view defaults via `defaults` (esp. `sortBy`); the page-1 default for `page`
 * is built in.
 */
export const commonFilterFields = {
  page: numberField('page', 1, { min: 1 }),
  query: stringField('query'),
  status: stringField('status'),
  statuses: stringArrayField('statuses'),
  createdByMe: booleanField('createdByMe'),
  sortBy: stringField('sortBy'),
  contentTypes: contentTypesField(),
  dateFrom: stringField('dateFrom'),
  dateTo: stringField('dateTo'),
  advertiserGroup: entityField<NonNullable<Filter['advertiserGroup']>>({
    idKey: 'advertiserGroupId',
    labelKey: 'advertiserGroupName',
    entityLabelKey: 'name',
  }),
  advertiser: entityField<NonNullable<Filter['advertiser']>>({
    idKey: 'advertiserId',
    labelKey: 'advertiserName',
    entityLabelKey: 'label',
  }),
  client: entityField<NonNullable<Filter['client']>>({
    idKey: 'clientId',
    labelKey: 'clientName',
    entityLabelKey: 'name',
  }),
  creator: entityField<NonNullable<Filter['creator']>>({
    idKey: 'creatorId',
    labelKey: 'creatorName',
    entityLabelKey: 'username',
  }),
  campaign: entityField<NonNullable<Filter['campaign']>>({
    idKey: 'campaignId',
    labelKey: 'campaignName',
    entityLabelKey: 'name',
  }),
} as const satisfies QueryFilterSchema<CommonFilterShape>

/**
 * Field names that should reset `page` back to 1 when the user changes them.
 * Mirrors the manual watch blocks in the legacy `FilterComponent` /
 * `ContentLibraryMediaFilters`. Typed as `string` so per-app filter types
 * (which extend the shared `Filter` shape) can consume it directly.
 */
export const COMMON_RESET_PAGE_ON: readonly string[] = [
  'query',
  'advertiserGroup',
  'advertiser',
  'client',
  'status',
  'statuses',
  'createdByMe',
  'sortBy',
  'creator',
  'campaign',
  'dateFrom',
  'dateTo',
  'source',
  'contentTypes',
]
